import { io, Socket } from 'socket.io-client';
import { captureMessage } from '@sentry/react';
import { withTimeout, TimeoutError } from '@voley/shared-utils/with-timeout';
import { reportWebSocketError } from '@/shared/utils/errorReporting';
import { config } from '@/config';
import { socketLogger } from '@/core/utils/logger';
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';
import { ClientEvents } from '@voley/shared-types/socket';
import {
  isSessionInvalidated as isSessionInvalidatedFlag,
  markSessionInvalidated as markSessionInvalidatedFlag,
  resetSessionInvalidated as resetSessionInvalidatedFlag,
} from '@/api/core/sessionManager';

// Queued emit for reliable message delivery
interface QueuedEmit {
  event: string;
  data: unknown;
  timestamp: number;
  id: string;
}

export class SocketClient {
  private static instance: SocketClient | null = null;
  private socket: Socket | null = null;
  private connected: boolean = false;
  private connecting: boolean = false;
  private lastToken: string | null = null;
  private prefetchedToken: string | null = null; // Pre-fetched token for immediate use
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3; // kept low to avoid reconnect spam
  private reconnectTimer?: NodeJS.Timeout;
  private readonly CONNECTION_TIMEOUT = 15000; // 15 seconds (increased for polling handshake)
  private readonly OPERATION_TIMEOUT = 5000; // 5 seconds for emit operations
  private currentUserId: string | null = null;
  private currentOrgId: string | null = null;
  private lastConnectionAttempt: number = 0;
  private readonly MIN_CONNECTION_INTERVAL = 3000; // Minimum 3 seconds between connection attempts
  private handlersAttached: boolean = false; // Guard against duplicate handler registration

  // Session ID unknown retry tracking — NOT reset per-iteration to prevent infinite loops
  private sessionUnknownRetries: number = 0;
  private readonly MAX_SESSION_UNKNOWN_RETRIES = 5;

  // Proactive auth refresh state
  private proactiveRefreshInProgress: boolean = false;
  private lastProactiveRefresh: number = 0;
  private readonly MIN_PROACTIVE_REFRESH_INTERVAL = 10000; // At least 10s between proactive refreshes
  private tokenExpiryCallbackRegistered: boolean = false;
  private consecutiveRefreshFailures: number = 0;
  private readonly MAX_REFRESH_FAILURES = 3; // After 3 failures, emit session_invalid event
  private tokenExpiryRegistrationPromise: Promise<void> | null = null; // Single-flight guard for registration

  // Legacy compatibility accessor - delegates to sessionManager SSOT.
  public get sessionInvalidated(): boolean {
    return isSessionInvalidatedFlag();
  }

  public set sessionInvalidated(value: boolean) {
    if (value) {
      markSessionInvalidatedFlag();
      return;
    }
    resetSessionInvalidatedFlag();
  }

  // Disconnect storm detection
  private disconnectTimestamps: number[] = [];
  private readonly STORM_THRESHOLD = 5;
  private readonly STORM_WINDOW_MS = 60_000;

  // Server-signaled busy period — clients add extra backoff during reconnect storms
  private serverBusyUntil: number = 0;

  // Reconnection authority callback - allows SocketProvider to own the reconnect flow
  private reconnectCallback: ((reason: string) => Promise<void>) | null = null;

  // Emit queue for reliable message delivery during connection establishment
  private emitQueue: QueuedEmit[] = [];
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly QUEUE_MAX_AGE_MS = 30000; // Discard queued events older than 30 seconds

  private constructor() {}

  public static getInstance(): SocketClient {
    if (!SocketClient.instance) {
      SocketClient.instance = new SocketClient();
      // Expose instance on window for sessionManager (avoids circular import).
      // Prefer SocketClient.getInstance() for all other callers.
      if (typeof window !== 'undefined') {
        (window as { __socketClient?: SocketClient }).__socketClient = SocketClient.instance;
      }
    }
    return SocketClient.instance;
  }

  public async connect(): Promise<Socket | null> {
    // If session is permanently invalidated, don't attempt connection
    if (this.sessionInvalidated) {
      socketLogger.info('[SocketClient] Session invalidated, skipping connect');
      return null;
    }

    // If server signaled busy (reconnect storm), wait until the retry-after period expires
    const busyNow = Date.now();
    if (this.serverBusyUntil > busyNow) {
      const busyWait = this.serverBusyUntil - busyNow;
      socketLogger.warn(`[SocketClient] Server busy, waiting ${busyWait}ms before connecting`);
      await new Promise(resolve => setTimeout(resolve, busyWait));
    }

    // RATE LIMIT: Prevent connection spam
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastConnectionAttempt;
    if (timeSinceLastAttempt < this.MIN_CONNECTION_INTERVAL) {
      const waitTime = this.MIN_CONNECTION_INTERVAL - timeSinceLastAttempt;
      socketLogger.warn(`[SocketClient] Rate limit: waiting ${waitTime}ms before next connection attempt`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    this.lastConnectionAttempt = Date.now();

    // If we're already connected, return existing socket
    if (this.connected && this.socket && this.socket.connected) {
      socketLogger.info('[SocketClient] Already connected, returning existing socket');
      return this.socket;
    }

    // If we're connecting, return null immediately - don't block UI
    // The caller should handle null gracefully and retry later
    if (this.connecting) {
      socketLogger.info('[SocketClient] Already connecting, returning null to avoid blocking');
      return null;
    }

    // If we have an existing socket, clean up handlers before creating new one
    if (this.socket) {
      socketLogger.info('[SocketClient] Cleaning up existing socket before new connection');
      this.socket.removeAllListeners();
      this.handlersAttached = false;
    }

    this.connecting = true;

    try {
      const wsUrl = config.websocket.url;

      // PRE-FETCH TOKEN BEFORE CONNECTING - CRITICAL for avoiding 400 errors
      // The server requires a valid JWT token in socket.handshake.auth.token
      // If the token is not ready when the handshake starts, the server returns 400
      socketLogger.info(`[SocketClient] Pre-fetching auth token before connection...`);
      try {
        const { wsAuthService } = await import('../auth/services/WebSocketAuthService');
        this.prefetchedToken = await wsAuthService.getToken();
        if (this.prefetchedToken) {
          socketLogger.info('[SocketClient] Token pre-fetched successfully');

          // Register for token expiry notifications (proactive refresh)
          // This ensures we get notified before the token expires so we can reconnect
          await this.registerTokenExpiryCallback();
        } else {
          socketLogger.warn('[SocketClient] Token pre-fetch returned null - aborting connection (auth required)');
          this.connecting = false;
          return null;
        }
      } catch (prefetchError) {
        socketLogger.error('[SocketClient] Token pre-fetch failed - aborting connection:', prefetchError);
        this.connecting = false;
        return null;
      }

      // Determine transport strategy:
      // - If forceWebSocketOnly: use websocket only (staging/prod - avoids multi-replica issues)
      // - Otherwise: start with polling, then upgrade to websocket (local dev)
      const forceWebSocketOnly = config.websocket.forceWebSocketOnly;
      const transports = forceWebSocketOnly ? ['websocket'] : ['polling', 'websocket'];
      const allowUpgrade = !forceWebSocketOnly;

      if (process.env.NODE_ENV === 'development') {

        socketLogger.debug('[SocketClient] Socket.IO config:', {

          url: wsUrl,
          path: config.websocket.config.path || '/socket.io',
          forceWebSocketOnly,
          transports,
          upgrade: allowUpgrade,
          hasToken: !!this.prefetchedToken,
        });

      }

      socketLogger.info(`[SocketClient] Creating socket connection`, {
        url: wsUrl,
        path: config.websocket.config.path || '/socket.io',
        forceWebSocketOnly,
        transports: transports.join(', '),
        upgrade: allowUpgrade,
        hasToken: !!this.prefetchedToken,
      });

      this.socket = io(wsUrl, {
        ...config.websocket.config,
        transports,
        upgrade: allowUpgrade,
        rememberUpgrade: allowUpgrade,
        auth: (callback) => {
          // SYNC callback - token is already pre-fetched, no async needed
          // This prevents race conditions where the handshake starts before token is ready
          if (this.prefetchedToken) {
            socketLogger.info('[SocketClient] Using pre-fetched token for auth callback');
            this.lastToken = this.prefetchedToken;
            callback({ token: this.prefetchedToken });
          } else {
            // This should not happen since we abort connection if token is null above
            socketLogger.error('[SocketClient] No token available in auth callback - this is unexpected');
            callback({ error: 'No authentication token available' });
          }
        },
      });

      // Set up connection event listeners - only once per socket instance
      if (!this.handlersAttached) {
        this.handlersAttached = true;
        socketLogger.info('[SocketClient] Attaching socket event handlers');

        this.socket.on('connect', () => {
          const transport = this.socket?.io?.engine?.transport?.name || 'unknown';
          const wasProactiveRefresh = this.proactiveRefreshInProgress;
          if (process.env.NODE_ENV === 'development') {
            socketLogger.info('[SocketClient] Connected', {
              id: this.socket?.id,
              transport,
              wasProactiveRefresh,
              reconnectAttempts: this.reconnectAttempts
            });
          }
          socketLogger.info('[SocketClient] Connected to server', {
            socketId: this.socket?.id,
            transport,
            wasProactiveRefresh
          });
          this.connected = true;
          this.connecting = false;
          this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
          this.sessionUnknownRetries = 0; // Reset session-unknown retries on successful connection
          this.prefetchedToken = null; // Clear pre-fetched token after successful connection
          this.proactiveRefreshInProgress = false; // Clear proactive refresh flag

          // Clear any pending reconnect timers
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
          }

          // Flush queued emits now that we're connected
          this.flushEmitQueue();
        });

        // Log transport upgrade events
        this.socket.io?.engine?.on('upgrade', (transport: { name: string }) => {
          if (process.env.NODE_ENV === 'development') {
            socketLogger.info('[SocketClient] Transport upgraded to:', transport?.name);
          }
          socketLogger.info('[SocketClient] Transport upgraded to:', transport?.name);
        });

        this.socket.on('disconnect', (reason) => {
          this.checkDisconnectStorm();
          const hadToken = !!this.lastToken;
          const socketId = this.socket?.id || 'unknown';
          const wasProactiveRefresh = this.proactiveRefreshInProgress;

          if (process.env.NODE_ENV === 'development') {
            socketLogger.warn('[SocketClient] Disconnected:', {
              reason,
              socketId,
              hadToken,
              wasProactiveRefresh,
              forceWebSocketOnly: config.websocket.forceWebSocketOnly,
            });
          }
          socketLogger.info('[SocketClient] Disconnected from server:', {
            reason,
            socketId,
            hadToken,
            wasProactiveRefresh,
          });

          // Persist disconnect info for debugging (dev only)
          if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
            window.__socketDisconnectReason = reason;
            window.__socketDisconnectTime = new Date().toISOString();
            try {
              const history = (getStorageItem(STORAGE_KEYS.SOCKET_DISCONNECT_HISTORY) as unknown[] || []) as Record<string, unknown>[];
              history.push({ reason, time: new Date().toISOString(), hadToken, socketId });
              // Keep only last 10 entries
              setStorageItem(STORAGE_KEYS.SOCKET_DISCONNECT_HISTORY, history.slice(-10));
            } catch (_e) { /* ignore */ }
          }
          this.connected = false;
          this.connecting = false;
          // Don't reset lastToken on disconnect to allow reconnection with same token

          // Auto-reconnect for unexpected disconnects (not client-initiated)
          const shouldReconnect = reason !== 'io client disconnect' && !this.sessionInvalidated;

          if (shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            const jitter = Math.random() * 1000;
            const totalDelay = backoffDelay + jitter;

            if (process.env.NODE_ENV === 'development') {
              socketLogger.info(`[SocketClient] Reconnecting in ${Math.round(totalDelay / 1000)}s (reason: ${reason})`);
            }
            socketLogger.info(`[SocketClient] Scheduling reconnection in ${Math.round(totalDelay / 1000)}s after disconnect (reason: ${reason})`);

            this.reconnectTimer = setTimeout(async () => {
              this.reconnectAttempts++;
              if (process.env.NODE_ENV === 'development') {
                socketLogger.info(`[SocketClient] Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
              }
              // Catch connection errors to prevent unhandled rejections
              this.connect().catch((err) => {
                socketLogger.warn('[SocketClient] Reconnection attempt failed:', err);
              });
            }, totalDelay);
          }
        });

        this.socket.on('connect_error', (error) => {
          // Enhanced diagnostic logging for connection errors
          const transport = this.socket?.io?.engine?.transport?.name || 'unknown';
          const engineError = (error as Error & { description?: unknown; context?: unknown })?.description || (error as Error & { context?: unknown })?.context || null;
          const hadToken = !!this.lastToken || !!this.prefetchedToken;
          const isAuthError = error.message?.includes('Authentication') ||
                             error.message?.includes('unauthorized') ||
                             error.message?.includes('token');

          if (process.env.NODE_ENV === 'development') {
            socketLogger.error('[SocketClient] Connection error:', {
              message: error.message,
              transport,
              engineError,
              wsUrl: config.websocket.url,
              hadToken,
              isAuthError,
              forceWebSocketOnly: config.websocket.forceWebSocketOnly,
            });
          }
          socketLogger.error('[SocketClient] Connection error:', {
            message: error.message,
            transport,
            engineError,
            hadToken,
            isAuthError,
          });

          // Store diagnostic info in localStorage for debugging (dev only)
          if (process.env.NODE_ENV === 'development') {
            try {
              const history = (getStorageItem(STORAGE_KEYS.SOCKET_CONNECT_ERRORS) as unknown[] || []) as Record<string, unknown>[];
              history.push({
                time: new Date().toISOString(),
                message: error.message,
                transport,
                engineError: engineError ? String(engineError) : null,
              });
              setStorageItem(STORAGE_KEYS.SOCKET_CONNECT_ERRORS, history.slice(-10));
            } catch (_e) { /* ignore */ }
          }

          this.connected = false;
          this.connecting = false;
          this.prefetchedToken = null; // Clear pre-fetched token on error

          // CRITICAL: Handle "Session ID unknown" error (code 1)
          // This happens when polling requests hit different Railway replicas
          // Force a clean reconnection by destroying the old socket completely
          const isSessionUnknown = error.message?.includes('Session ID unknown') ||
                                   (error as Error & { data?: { code?: number } })?.data?.code === 1 ||
                                   engineError?.toString?.()?.includes('Session ID unknown');

          if (isSessionUnknown) {
            this.sessionUnknownRetries++;

            if (this.sessionUnknownRetries >= this.MAX_SESSION_UNKNOWN_RETRIES) {
              socketLogger.error('[SocketClient] Max session-unknown retries reached, stopping reconnect attempts');
              if (process.env.NODE_ENV === 'development') {
                socketLogger.error('[SocketClient] Max session-unknown retries reached (' + this.sessionUnknownRetries + ')');
              }
              this.reconnectAttempts = 0;
              this.lastToken = null;
              return; // Stop retrying — let user see reconnect-failed state
            }

            if (process.env.NODE_ENV === 'development') {
              socketLogger.warn('[SocketClient] Session ID unknown - forcing clean reconnection (' + this.sessionUnknownRetries + '/' + this.MAX_SESSION_UNKNOWN_RETRIES + ')');
            }
            socketLogger.warn('[SocketClient] Session ID unknown error - multi-replica routing issue detected', {
              attempt: this.sessionUnknownRetries,
              maxAttempts: this.MAX_SESSION_UNKNOWN_RETRIES
            });

            // Destroy the current socket completely to clear stale session
            if (this.socket) {
              try {
                this.socket.io?.engine?.close();
              } catch { /* ignore */ }
              this.socket.removeAllListeners();
              this.socket.disconnect();
              this.socket = null;
            }

            // Reset state for clean reconnection
            this.handlersAttached = false;
            this.reconnectAttempts = 0; // Reset because we're starting fresh

            // Reconnect with rate limiting — respects MIN_CONNECTION_INTERVAL to prevent storm
            // (connect() enforces the rate limit internally)
            const retryDelay = Math.max(this.MIN_CONNECTION_INTERVAL, 1000 * this.sessionUnknownRetries);
            socketLogger.info(`[SocketClient] Starting fresh connection after session error in ${retryDelay}ms`);
            setTimeout(() => {
              this.connect().catch((err) => {
                socketLogger.warn('[SocketClient] Fresh reconnection failed:', err);
              });
            }, retryDelay);
            return; // Don't continue with normal reconnect logic
          }

          // Implement custom exponential backoff for other errors
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            const jitter = Math.random() * 1000;
            const totalDelay = backoffDelay + jitter;

            socketLogger.info(`[SocketClient] Reconnecting in ${Math.round(totalDelay / 1000)}s (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

            this.reconnectTimer = setTimeout(async () => {
              this.reconnectAttempts++;
              socketLogger.info(`[SocketClient] Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
              // Catch connection errors to prevent unhandled rejections
              this.connect().catch((err) => {
                socketLogger.warn('[SocketClient] Reconnection attempt failed:', err);
              });
            }, totalDelay);
          } else {
            socketLogger.error('[SocketClient] Max reconnection attempts reached');
            this.reconnectAttempts = 0;
            this.lastToken = null;
          }
        });

        this.socket.on('reconnect', (attemptNumber) => {
          socketLogger.info('[SocketClient] Reconnected to server after', attemptNumber, 'attempts');
          this.connected = true;
          this.connecting = false;
        });

        this.socket.on('reconnect_error', (error) => {
          socketLogger.error('[SocketClient] Reconnection error:', error);
        });

        this.socket.on('reconnect_failed', () => {
          socketLogger.error('[SocketClient] Failed to reconnect to server');
          this.connected = false;
          this.connecting = false;
          this.lastToken = null;
        });
      } // End of handlersAttached guard

      // Wait for initial connection with timeout
      return await withTimeout({
        promise: new Promise<Socket | null>((resolve, reject) => {
          if (!this.socket) {
            this.connecting = false;
            reject(new Error('Socket instance is null'));
            return;
          }

          this.socket.once('connect', () => {
            resolve(this.socket);
          });

          this.socket.once('connect_error', (error) => {
            this.connecting = false;
            this.lastToken = null;
            reject(error);
          });
        }),
        ms: this.CONNECTION_TIMEOUT,
        message: 'WebSocket connection timeout',
      }).catch((error) => {
        if (error instanceof TimeoutError) {
          this.connecting = false;
          this.lastToken = null;
          reportWebSocketError('connection_timeout', error);
        }
        throw error;
      });
    } catch (error) {
      socketLogger.error('[SocketClient] Failed to create socket connection:', error);
      this.connecting = false;
      this.lastToken = null;
      return null;
    }
  }

  /**
   * Detect disconnect storms (too many disconnects in a short window).
   * Captures to Sentry and resets the window to avoid repeated alerts.
   */
  private checkDisconnectStorm(): void {
    const now = Date.now();
    this.disconnectTimestamps.push(now);
    this.disconnectTimestamps = this.disconnectTimestamps.filter(t => now - t < this.STORM_WINDOW_MS);
    if (this.disconnectTimestamps.length >= this.STORM_THRESHOLD) {
      captureMessage('WebSocket disconnect storm detected', {
        level: 'error',
        tags: { error_type: 'ws_disconnect_storm' },
        extra: { disconnectsInWindow: this.disconnectTimestamps.length },
      });
      this.disconnectTimestamps = [];
    }
  }

  public disconnect(): void {
    if (process.env.NODE_ENV === 'development') {
      socketLogger.warn('[SocketClient] disconnect() CALLED - stack:', new Error().stack);
      try {
        const history = (getStorageItem(STORAGE_KEYS.SOCKET_DISCONNECT_CALLS) as unknown[] || []) as Record<string, unknown>[];
        history.push({
          time: new Date().toISOString(),
          stack: new Error().stack?.split('\n').slice(1, 6).join('\n')
        });
        setStorageItem(STORAGE_KEYS.SOCKET_DISCONNECT_CALLS, history.slice(-10));
      } catch (_e) { /* ignore */ }
    }

    // Clear any pending reconnection timers FIRST to prevent ghost reconnections
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    // Clear emit queue to avoid stale events on next connection
    if (this.emitQueue.length > 0) {
      socketLogger.info(`[SocketClient] Clearing ${this.emitQueue.length} queued emits on disconnect`);
      this.emitQueue = [];
    }

    if (this.socket) {
      socketLogger.info('[SocketClient] Disconnecting socket');

      // Remove all listeners to prevent memory leaks
      this.socket.removeAllListeners();

      // Presence cleanup handled by PresenceInitializer
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.connecting = false;
      this.lastToken = null;
      this.prefetchedToken = null;
      this.currentUserId = null;
      this.currentOrgId = null;
      this.handlersAttached = false; // Reset so handlers can be attached on next connect
    }

    // Reset reconnection state
    this.reconnectAttempts = 0;
    this.sessionUnknownRetries = 0;

    // Reset proactive refresh state
    this.proactiveRefreshInProgress = false;
    this.consecutiveRefreshFailures = 0;

    // Clear token expiry callback AND timer to prevent stale notifications
    // (callback and timer will be re-registered on next connect)
    if (this.tokenExpiryCallbackRegistered) {
      import('../auth/services/WebSocketAuthService').then(({ wsAuthService }) => {
        wsAuthService.onTokenExpiring(null);
        wsAuthService.clearToken(); // Also clears the refresh timer
      }).catch(() => { /* ignore */ });
      this.tokenExpiryCallbackRegistered = false;
    }
  }

  public getSocket(): Socket | null {
    return this.socket;
  }

  public isConnected(): boolean {
    return this.connected && this.socket?.connected === true;
  }

  /**
   * Proactively refresh authentication and reconnect the socket.
   * Called by WebSocketAuthService when token is about to expire.
   *
   * This method:
   * 1. Rate-limits to prevent reconnect storms
   * 2. Fetches a fresh token using forceRefresh
   * 3. Delegates reconnection to SocketProvider via callback (respects single-flight + versioning)
   * 4. Emits auth:session_invalid event on max failures (never redirects directly)
   *
   * @param reason - Why the refresh is being triggered (for logging)
   */
  public async refreshAuthAndReconnect(reason: string): Promise<void> {
    // If session is permanently invalidated, don't attempt refresh
    if (this.sessionInvalidated) {
      socketLogger.info('[SocketClient] Session invalidated, skipping refreshAuthAndReconnect');
      return;
    }

    const now = Date.now();

    // Rate limit: prevent refresh storms
    const timeSinceLastRefresh = now - this.lastProactiveRefresh;
    if (timeSinceLastRefresh < this.MIN_PROACTIVE_REFRESH_INTERVAL) {
      socketLogger.warn(`[SocketClient] Proactive refresh rate-limited (${timeSinceLastRefresh}ms since last), skipping`);
      return;
    }

    // Guard against concurrent proactive refreshes
    if (this.proactiveRefreshInProgress) {
      socketLogger.info('[SocketClient] Proactive refresh already in progress, skipping');
      return;
    }

    // Skip if we're already in a connection attempt
    if (this.connecting) {
      socketLogger.info('[SocketClient] Connection already in progress, deferring proactive refresh');
      return;
    }

    this.proactiveRefreshInProgress = true;
    this.lastProactiveRefresh = now;

    if (process.env.NODE_ENV === 'development') {
      socketLogger.info('[SocketClient] PROACTIVE AUTH REFRESH starting:', {
        reason,
        wasConnected: this.connected,
        socketId: this.socket?.id,
        hasReconnectCallback: !!this.reconnectCallback
      });
    }
    socketLogger.info('[SocketClient] Starting proactive auth refresh', { reason });

    try {
      // Step 1: Get fresh token with failure classification
      socketLogger.info('[SocketClient] Fetching fresh token for proactive refresh...');
      const { wsAuthService } = await import('../auth/services/WebSocketAuthService');
      const tokenResult = await wsAuthService.getTokenWithStatus({ forceRefresh: true });

      if (!tokenResult.token) {
        this.consecutiveRefreshFailures++;

        if (tokenResult.failureReason === 'session_expired') {
          // On the FIRST session_expired failure, schedule a retry after 5 seconds.
          // This gives the access token refresh chain time to complete if another
          // request triggered it (e.g. baseQueryWithRetry's single-flight refresh).
          if (this.consecutiveRefreshFailures < 2) {
            socketLogger.warn('[SocketClient] First session_expired failure — scheduling retry in 5s', {
              consecutiveFailures: this.consecutiveRefreshFailures
            });
            if (process.env.NODE_ENV === 'development') {
              socketLogger.warn('[SocketClient] First session_expired — will retry in 5s');
            }

            // Schedule a retry — don't invalidate yet
            setTimeout(() => {
              if (this.sessionInvalidated) return; // Already handled
              socketLogger.info('[SocketClient] Retrying proactive refresh after session_expired delay');
              this.proactiveRefreshInProgress = false; // Allow the retry
              this.refreshAuthAndReconnect('session_expired_retry').catch(err => {
                socketLogger.error('[SocketClient] Scheduled retry failed:', err);
              });
            }, 5000);
            return;
          }

          // 2+ consecutive session_expired failures — session is truly gone
          socketLogger.error('[SocketClient] Repeated session_expired failures, marking invalidated', {
            consecutiveFailures: this.consecutiveRefreshFailures
          });
          wsAuthService.markSessionInvalidated();
          this.sessionInvalidated = true;

          // Clear reconnect timer to stop further attempts
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
          }

          captureMessage('Auth refresh loop: session invalidated', {
            level: 'error',
            tags: { error_type: 'auth_refresh_loop', failure_reason: 'session_expired' },
            extra: { consecutiveFailures: this.consecutiveRefreshFailures },
          });

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:session_invalid', {
              detail: {
                reason: 'session_expired',
                failures: this.consecutiveRefreshFailures,
                timestamp: Date.now()
              }
            }));
          }
          return;
        }

        // Transient or unknown failure — use counter-based approach
        socketLogger.error('[SocketClient] Failed to get fresh token during proactive refresh', {
          consecutiveFailures: this.consecutiveRefreshFailures,
          maxFailures: this.MAX_REFRESH_FAILURES,
          failureReason: tokenResult.failureReason
        });
        if (process.env.NODE_ENV === 'development') {
          socketLogger.error('[SocketClient] Proactive refresh failed - no token', {
            failures: this.consecutiveRefreshFailures,
            failureReason: tokenResult.failureReason
          });
        }

        // After repeated transient failures, emit session_invalid event
        if (this.consecutiveRefreshFailures >= this.MAX_REFRESH_FAILURES) {
          socketLogger.error('[SocketClient] Max refresh failures reached, emitting session_invalid event');
          wsAuthService.markSessionInvalidated();
          this.sessionInvalidated = true;

          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
          }

          captureMessage('Auth refresh loop: session invalidated', {
            level: 'error',
            tags: { error_type: 'auth_refresh_loop', failure_reason: 'max_refresh_failures' },
            extra: { consecutiveFailures: this.consecutiveRefreshFailures },
          });

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:session_invalid', {
              detail: {
                reason: 'max_refresh_failures',
                failures: this.consecutiveRefreshFailures,
                timestamp: Date.now()
              }
            }));
          }
        }
        return;
      }

      // Reset failure counter on success
      this.consecutiveRefreshFailures = 0;
      const newToken = tokenResult.token;
      socketLogger.info('[SocketClient] Fresh token obtained for proactive refresh');

      // Step 2: Store the fresh token for the next connection
      this.prefetchedToken = newToken;

      // Step 3: If reconnect callback is registered, use it (goes through SocketProvider's single-flight)
      if (this.reconnectCallback) {
        socketLogger.info('[SocketClient] Delegating reconnect to SocketProvider via callback');

        // Disconnect current socket first
        if (this.socket && this.connected) {
          socketLogger.info('[SocketClient] Disconnecting current socket before callback reconnect');
          this.socket.disconnect();

          // Small delay to ensure clean disconnect
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Delegate to SocketProvider's reconnect flow
        await this.reconnectCallback(reason);

        if (process.env.NODE_ENV === 'development') {
          socketLogger.info('[SocketClient] PROACTIVE AUTH REFRESH complete via callback');
        }
        socketLogger.info('[SocketClient] Proactive auth refresh complete (via callback)');
        return;
      }

      // Fallback: Direct reconnect if no callback registered (legacy path)
      // This should rarely happen since SocketProvider always registers the callback
      socketLogger.warn('[SocketClient] No reconnect callback registered, using direct reconnect (legacy)');

      if (this.socket && this.connected) {
        socketLogger.info('[SocketClient] Performing controlled disconnect for auth refresh (legacy)');

        // Disconnect current socket
        this.socket.disconnect();

        // Small delay to ensure clean disconnect before reconnect
        await new Promise(resolve => setTimeout(resolve, 100));

        // Check if socket was destroyed during the delay (e.g., by logout)
        if (!this.socket) {
          socketLogger.info('[SocketClient] Socket destroyed during proactive refresh, aborting reconnect');
          return;
        }

        // Reconnect with fresh token
        socketLogger.info('[SocketClient] Reconnecting with fresh token (legacy)...');
        this.socket.connect();

        if (process.env.NODE_ENV === 'development') {
          socketLogger.info('[SocketClient] PROACTIVE AUTH REFRESH complete - reconnecting (legacy)');
        }
        socketLogger.info('[SocketClient] Proactive auth refresh complete (legacy)');
      } else if (!this.connected && !this.connecting) {
        // Not connected - just ensure token is ready for next connect
        socketLogger.info('[SocketClient] Socket not connected, fresh token cached for next connect');
      }
    } catch (error) {
      socketLogger.error('[SocketClient] Error during proactive auth refresh:', error);
      if (process.env.NODE_ENV === 'development') {
        socketLogger.error('[SocketClient] Proactive refresh error:', error);
      }
    } finally {
      this.proactiveRefreshInProgress = false;
    }
  }

  /**
   * Register a callback that handles reconnection through the connection authority (SocketProvider).
   * This ensures all reconnects go through single-flight + versioning guards.
   */
  public setReconnectCallback(callback: ((reason: string) => Promise<void>) | null): void {
    this.reconnectCallback = callback;
    socketLogger.info('[SocketClient] Reconnect callback registered:', !!callback);
  }

  /**
   * Mark the server as busy for a duration.
   * Called when the server emits SERVER_BUSY during reconnect storms.
   * connect() respects this by adding extra delay before connection attempts.
   */
  public setServerBusyUntil(timestamp: number): void {
    this.serverBusyUntil = timestamp;
    socketLogger.info('[SocketClient] Server busy until', { until: new Date(timestamp).toISOString() });
  }

  /**
   * Register for token expiry notifications from WebSocketAuthService.
   * Uses single-flight pattern to prevent race conditions from concurrent calls.
   */
  private async registerTokenExpiryCallback(): Promise<void> {
    // Already registered - early return
    if (this.tokenExpiryCallbackRegistered) {
      return;
    }

    // Single-flight: if registration is in progress, wait for it
    if (this.tokenExpiryRegistrationPromise) {
      return this.tokenExpiryRegistrationPromise;
    }

    // Start registration with single-flight guard
    this.tokenExpiryRegistrationPromise = (async () => {
      try {
        const { wsAuthService } = await import('../auth/services/WebSocketAuthService');
        wsAuthService.onTokenExpiring((reason: string) => {
          socketLogger.info('[SocketClient] Received token expiry notification:', reason);
          // Trigger proactive refresh (this method handles rate limiting internally)
          this.refreshAuthAndReconnect(reason).catch(err => {
            socketLogger.error('[SocketClient] Proactive refresh failed:', err);
          });
        });
        this.tokenExpiryCallbackRegistered = true;
        socketLogger.info('[SocketClient] Token expiry callback registered');
      } catch (error) {
        socketLogger.error('[SocketClient] Failed to register token expiry callback:', error);
      } finally {
        this.tokenExpiryRegistrationPromise = null;
      }
    })();

    return this.tokenExpiryRegistrationPromise;
  }

  // Room management helpers
  public joinRoom(roomName: string, data?: unknown): void {
    if (this.isConnected() && this.socket) {
      this.socket.emit(ClientEvents.JOIN_ROOM, { room: roomName, ...(data as Record<string, any> || {}) });
    }
  }

  public leaveRoom(roomName: string, data?: unknown): void {
    if (this.isConnected() && this.socket) {
      this.socket.emit(ClientEvents.LEAVE_ROOM, { room: roomName, ...(data as Record<string, any> || {}) });
    }
  }

  // Flush queued emits when socket connects
  private flushEmitQueue(): void {
    if (this.emitQueue.length === 0) return;

    const now = Date.now();
    const validEmits = this.emitQueue.filter(
      (item) => now - item.timestamp < this.QUEUE_MAX_AGE_MS
    );

    socketLogger.info(`[SocketClient] Flushing ${validEmits.length} queued emits (${this.emitQueue.length - validEmits.length} expired)`);

    for (const item of validEmits) {
      try {
        this.socket?.emit(item.event, item.data);
        socketLogger.debug(`[SocketClient] Flushed queued emit: ${item.event}`);
      } catch (error) {
        socketLogger.error(`[SocketClient] Failed to flush queued emit: ${item.event}`, error);
      }
    }

    // Clear the queue
    this.emitQueue = [];
  }

  // Add event to queue (for use during connection establishment)
  private queueEmit(event: string, data: unknown): boolean {
    // Don't queue if already at max size
    if (this.emitQueue.length >= this.MAX_QUEUE_SIZE) {
      socketLogger.warn(`[SocketClient] Emit queue full, dropping event: ${event}`);
      return false;
    }

    const queuedEmit: QueuedEmit = {
      event,
      data,
      timestamp: Date.now(),
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    };

    this.emitQueue.push(queuedEmit);
    socketLogger.info(`[SocketClient] Queued emit '${event}' (${this.emitQueue.length} in queue)`);
    return true;
  }

  // Check if event type should be queued when not connected
  private isQueueableEvent(event: string): boolean {
    // Queue critical messaging events that should not be dropped
    const queueableEvents = [
      'send_message',
      'send_direct_message',
      'send_group_message',
      'mark_read',
      'typing',
      'team_member_typing',
      'join_conversation',
      'leave_conversation',
      'join_team_group',
      'leave_team_group'
    ];
    return queueableEvents.includes(event);
  }

  // Generic emit helper with optional acknowledgment and timeout
  public emit(event: string, data?: unknown): void {
    if (this.isConnected() && this.socket) {
      try {
        this.socket.emit(event, data);
      } catch (error) {
        reportWebSocketError(`emit_${event}`, error);
        throw error;
      }
    } else if (this.connecting && this.isQueueableEvent(event)) {
      // Queue critical events during connection establishment
      const queued = this.queueEmit(event, data);
      if (queued) {
        socketLogger.info(`[SocketClient] Event '${event}' queued while connecting`);
      } else {
        socketLogger.warn(`[SocketClient] Failed to queue '${event}' - queue full`);
        reportWebSocketError(`emit_${event}_queue_full`, new Error('Emit queue full'));
      }
    } else {
      socketLogger.warn(`[SocketClient] Cannot emit '${event}' - socket not connected`);
      reportWebSocketError(`emit_${event}_disconnected`, new Error('Socket not connected'));
    }
  }

  // Generic listener helper
  public on(event: string, callback: (...args: unknown[]) => void): void {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  // Remove listener helper
  public off(event: string, callback?: (...args: unknown[]) => void): void {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  // User context management (presence handled by PresenceInitializer)
  public setUserContext(userId: string | null, orgId: string | null): void {
    this.currentUserId = userId;
    this.currentOrgId = orgId;
  }

  // Organization room helpers
  public joinOrganizationRoom(orgId: string): void {
    if (this.isConnected() && orgId) {
      const roomName = `org:${orgId}`;
      socketLogger.info(`[SocketClient] Joining organization room: ${roomName}`);
      this.emit(ClientEvents.JOIN_ROOM, { room: roomName });
    }
  }

  public leaveOrganizationRoom(orgId: string): void {
    if (this.isConnected() && orgId) {
      const roomName = `org:${orgId}`;
      socketLogger.info(`[SocketClient] Leaving organization room: ${roomName}`);
      this.emit(ClientEvents.LEAVE_ROOM, { room: roomName });
    }
  }
} 